import type { Aabb, InteriorTemplate, StaticCollider } from "@townsy/contracts";
import { YAW_NORTH, YAW_SOUTH, aabb } from "../geometry";
import { fixture } from "./common";

/**
 * Townsy Sports Center room (ADR-0016): one indoor hall, 100 x 60 m. Local coordinates: the entrance and exit door are
 * centred on the south wall (z = -25) and the hall opens north. West: a small-sided soccer pitch with ONE goal (north
 * end) guarded by the AI goalie. Centre: a basketball court with two hoops. East: two tennis courts. The lobby by the
 * door has the information kiosk; benches line the walls.
 *
 * Everything the game (apps/realtime/src/venues/sportsCenter) and the renderer (apps/web/src/venues/sportsCenter) need
 * to agree on lives here: court rectangles, hoop positions and heights, the goal mouth, net lines and serve positions.
 * Colliders are the benches, the kiosk, the hoop posts, the tennis net posts and the goal frame (the room bounds are
 * the walls); the playing surfaces are never colliders. Every collider id starts with "venue_": the venue's own 3D
 * draws them (the generic interior furniture skips that prefix).
 */
export const SPORTS_HALL: Aabb = aabb(-50, -25, 50, 35);

/** Basketball: court 15 m (x) by 28 m (z), hoops at both ends, rim 3.05 m high. */
export const SPORTS_BASKETBALL = {
  court: aabb(-7.5, 4, 7.5, 32),
  centre: { x: 0, z: 18 },
  rimHeight: 3.05,
  rimRadius: 0.23,
  /** Horizontal distance from the hoop centre beyond which a make scores 3 (the painted arc). */
  threePointRadius: 6.75,
  /** Distance of the hoop centre from its baseline (the backboard is 1.2 m inside it). */
  hoopFromBaseline: 1.575,
  backboard: { fromBaseline: 1.2, width: 1.8, height: 1.05, bottom: 2.9 },
  /** Free-throw line distance from the baseline, and the width of the key. */
  freeThrowFromBaseline: 5.8,
  keyWidth: 4.9,
  centreCircleRadius: 1.8,
  /** Hoop stanchions stand this far behind each baseline. */
  postBehindBaseline: 1.0,
  hoops: [
    // South hoop (faces north into the court) and north hoop (faces south).
    { id: 0, x: 0, z: 4 + 1.575, baselineZ: 4, facesNorth: true },
    { id: 1, x: 0, z: 32 - 1.575, baselineZ: 32, facesNorth: false },
  ],
} as const;

/** Soccer: pitch 24 m (x) by 40 m (z) with ONE goal on its north line, 5 m wide and 2 m high. */
export const SPORTS_SOCCER = {
  pitch: aabb(-45, -12, -21, 28),
  centreX: -33,
  halfwayZ: 8,
  centreCircleRadius: 3,
  goal: { x: -33, lineZ: 28, halfWidth: 2.5, height: 2.0, depth: 1.6, postRadius: 0.06 },
  /** The ball goes back here after a goal, a save or when it leaves the pitch (8 m from the goal line). */
  penaltySpot: { x: -33, z: 20 },
  /** The AI goalie moves along this line, just in front of the goal line. */
  goalieLineZ: 27.45,
  penaltyArea: aabb(-40, 18, -26, 28),
  goalArea: aabb(-37, 25, -29, 28),
} as const;

export interface TennisCourt {
  id: number;
  /** Doubles court (lines included): 11 m (x) by 24 m (z). */
  court: Aabb;
  centreX: number;
  netZ: number;
  /** Side 0 is the south half (z < netZ), side 1 the north half; each with its run-off. */
  sideAreas: readonly [Aabb, Aabb];
  /** Where each side serves from and where the AI waits (just behind its baseline). */
  serve: readonly [{ x: number; z: number }, { x: number; z: number }];
  /** Net posts (0.9 m outside the doubles sidelines). */
  netPosts: readonly [{ x: number; z: number }, { x: number; z: number }];
}

const TENNIS_RUNOFF_BACK = 6;
const TENNIS_RUNOFF_SIDE = 3;

function tennisCourt(id: number, x0: number): TennisCourt {
  const x1 = x0 + 11;
  const z0 = -8;
  const z1 = 16;
  const netZ = (z0 + z1) / 2;
  const cx = (x0 + x1) / 2;
  return {
    id,
    court: aabb(x0, z0, x1, z1),
    centreX: cx,
    netZ,
    sideAreas: [
      aabb(x0 - TENNIS_RUNOFF_SIDE, z0 - TENNIS_RUNOFF_BACK, x1 + TENNIS_RUNOFF_SIDE, netZ),
      aabb(x0 - TENNIS_RUNOFF_SIDE, netZ, x1 + TENNIS_RUNOFF_SIDE, z1 + TENNIS_RUNOFF_BACK),
    ],
    serve: [
      { x: cx, z: z0 - 0.6 },
      { x: cx, z: z1 + 0.6 },
    ],
    netPosts: [
      { x: x0 - 0.9, z: netZ },
      { x: x1 + 0.9, z: netZ },
    ],
  };
}

/** Tennis: two courts side by side, nets east-west (rules in the game: first to 7 points, win by 2). */
export const SPORTS_TENNIS = {
  courts: [tennisCourt(0, 15), tennisCourt(1, 33)] as readonly TennisCourt[],
  netHeight: 0.95,
  /** Singles sidelines are 1.37 m inside the doubles sidelines; service lines 6.4 m from the net. */
  singlesInset: 1.37,
  serviceLineFromNet: 6.4,
  runoffBack: TENNIS_RUNOFF_BACK,
  runoffSide: TENNIS_RUNOFF_SIDE,
} as const;

/** The information kiosk in the lobby, 7 m in front of the door: a directory board facing the entrance. */
export const SPORTS_KIOSK: Aabb = aabb(-2.7, -17.85, 2.7, -14.15);

/** Benches along the walls and beside the basketball court (seat 0.45 m high, 0.6 m deep). */
export const SPORTS_BENCHES: readonly { id: string; box: Aabb }[] = [
  { id: "venue_bench_sw", box: aabb(-30, -24.4, -8, -23.8) },
  { id: "venue_bench_se", box: aabb(8, -24.4, 30, -23.8) },
  { id: "venue_bench_w", box: aabb(-49.4, -8, -48.8, 24) },
  { id: "venue_bench_e", box: aabb(48.8, -10, 49.4, 18) },
  { id: "venue_bench_bw", box: aabb(-10.9, 12, -10.3, 24) },
  { id: "venue_bench_be", box: aabb(10.3, 12, 10.9, 24) },
];

/** Scoreboards on stands along the north wall (renderer only): centre x and width. */
export const SPORTS_SCOREBOARDS = {
  soccer: { x: SPORTS_SOCCER.goal.x, width: 8 },
  basketball: { x: 0, width: 7 },
  tennis: [
    { x: 20.5, width: 7 },
    { x: 38.5, width: 7 },
  ],
} as const;

/** Is (x, z) inside the box grown by `margin` metres? */
export function insideSportsBox(b: Aabb, x: number, z: number, margin = 0): boolean {
  return x >= b.minX - margin && x <= b.maxX + margin && z >= b.minZ - margin && z <= b.maxZ + margin;
}

/** The goal's posts and nets as three thin walls (open to the pitch). */
function goalColliders(): StaticCollider[] {
  const g = SPORTS_SOCCER.goal;
  const left = g.x - g.halfWidth - g.postRadius;
  const right = g.x + g.halfWidth + g.postRadius;
  const back = g.lineZ + g.depth;
  return [
    fixture("venue_goal_l", aabb(left - 0.08, g.lineZ - 0.08, left + 0.08, back + 0.05)),
    fixture("venue_goal_r", aabb(right - 0.08, g.lineZ - 0.08, right + 0.08, back + 0.05)),
    fixture("venue_goal_back", aabb(left - 0.08, back - 0.05, right + 0.08, back + 0.05)),
  ];
}

/** Hoop stanchion position (behind its baseline). */
export function sportsHoopPost(h: (typeof SPORTS_BASKETBALL.hoops)[number]): { x: number; z: number } {
  return { x: h.x, z: h.facesNorth ? h.baselineZ - SPORTS_BASKETBALL.postBehindBaseline : h.baselineZ + SPORTS_BASKETBALL.postBehindBaseline };
}

export function sportsCenterColliders(): StaticCollider[] {
  const out: StaticCollider[] = [];
  for (const b of SPORTS_BENCHES) out.push(fixture(b.id, b.box));
  out.push(fixture("venue_kiosk", SPORTS_KIOSK));
  for (const h of SPORTS_BASKETBALL.hoops) {
    const p = sportsHoopPost(h);
    out.push(fixture(`venue_hoop_post_${h.id}`, aabb(p.x - 0.3, p.z - 0.3, p.x + 0.3, p.z + 0.3)));
  }
  for (const c of SPORTS_TENNIS.courts) {
    c.netPosts.forEach((p, i) => out.push(fixture(`venue_net_post_${c.id}_${i}`, aabb(p.x - 0.1, p.z - 0.1, p.x + 0.1, p.z + 0.1))));
  }
  out.push(...goalColliders());
  return out;
}

export function sportsCenterTemplate(): InteriorTemplate {
  const b = SPORTS_HALL;
  return {
    id: "sports_center",
    venue: "sports_center",
    bounds: b,
    spawn: { position: { x: 0, z: b.minZ + 2.4 }, facing: YAW_NORTH },
    exit: { position: { x: 0, z: b.minZ + 1 }, facing: YAW_SOUTH },
    exitDoor: { position: { x: 0, z: b.minZ }, direction: { x: 0, z: -1 }, halfWidth: 1.6 },
    // The name board on the north wall, between the soccer scoreboard and the basketball scoreboard (5:1, the storefront sign's shape).
    signAnchor: { kind: "storefront", position: { x: -16, y: 4.4, z: b.maxZ - 0.1 }, facing: YAW_SOUTH, width: 14, height: 2.8 },
    colliders: sportsCenterColliders(),
  };
}
